import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory } from "react-router";
import Autosuggest from "react-autosuggest";
import { ReactComponent as SearchIcon } from "../svg/magnifying-glass-searchbar.svg";
import { useBestSearchMatch, useSearchBarPages } from "../hooks/deadlock/search";
import { useAutoFocus } from "../hooks/general-hooks";

function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function useSuggestions() {
  const { data: searchBarPages, loading, error } = useSearchBarPages();

  return (value) => {
    if (!searchBarPages || loading || error) {
      return [];
    }

    const escapedValue = escapeRegexCharacters(value.trim());
    if (escapedValue === "") {
      return [];
    }

    const regex = new RegExp(escapedValue, "i");
    const suggestions = searchBarPages
      .map((section) => ({
        title: section.title,
        pages: section.pages.filter((page) => regex.test(page.key)),
      }))
      .filter((section) => section.pages.length > 0);

    return suggestions;
  };
}

function getSuggestionValue(suggestion) {
  return suggestion.displayName;
}

function useRenderSuggestion() {
  return (suggestion) => {
    const img = suggestion.imgKey ? <img src={suggestion.imgKey} alt={suggestion.displayName} /> : null;
    return (
      <div className="render-suggestion">
        {img}
        <div className="suggest-text">{suggestion.displayName}</div>
      </div>
    );
  };
}

function renderSectionTitle(section) {
  return <strong>{section.title}</strong>;
}

function getSectionSuggestions(section) {
  return section.pages;
}

function getRecentlySearched() {
  if (typeof window !== "undefined") {
    const recentlySearched = localStorage.getItem("recentlySearchedHeroes");
    if (recentlySearched !== null) {
      return [
        {
          title: "Recently Searched",
          pages: JSON.parse(recentlySearched),
        },
      ];
    }
  }
  return [];
}

export default function Searchbar(props) {
  const { placeholder, autoFocus = false, className } = props;

  // Browser info
  const history = useHistory();

  // Input variables
  const inputRef = useAutoFocus({ disable: !autoFocus });
  const [value, setValue] = useState("");
  const [isInteracting, setInteraction] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(null);

  // Autosuggest state
  const [suggestions, setSuggestions] = useState(getRecentlySearched());
  const [lastPage, setLastPage] = useState(undefined);

  // Autosuggest functions
  const getBestSearchMatch = useBestSearchMatch();
  const getSuggestions = useSuggestions();
  const renderSuggestion = useRenderSuggestion();

  const onChange = (_event, { newValue, method }) => {
    if (method === "type") {
      !isInteracting && setInteraction(true);
    }
    setValue(newValue);
  };

  const onClick = () => {
    !isInteracting && setInteraction(true);
  };

  const onBlur = () => {
    setInteraction(false);
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    let suggestions = getSuggestions(value);
    if (value.trim().length === 0) {
      suggestions = getRecentlySearched();
    }
    setSuggestions(suggestions);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionHighlighted = ({ suggestion }) => {
    setHighlightedSuggestion(suggestion);
  };

  const onSuggestionSelected = (_event, { suggestion, suggestionValue, method }) => {
    if (suggestionValue && (method === "enter" || method === "click")) {
      if (suggestion.url) {
        const fullPath = `/deadlock/${suggestion.url.replace(/^\//, "")}`; // Prepend `/deadlock/` and ensure no leading slash
        history.push({
          pathname: fullPath,
        });
      } else {
        search(suggestionValue);
      }
      inputRef && inputRef.current.blur();
    }
  };

  const search = (searchValue) => {
    if (searchValue.trim() === "") return;

    const matches = getBestSearchMatch(searchValue);
    if (matches.length) {
      const topResult = matches[0].item;
      const fullPath = `/deadlock/${topResult.url.replace(/^\//, "")}`; // Make full path absolute
      history.push({
        pathname: fullPath,
      });

      if (window) {
        const recentlySearched = localStorage.getItem("recentlySearchedHeroes");
        const newEntry = topResult;
        if (recentlySearched === null) {
          localStorage.setItem("recentlySearchedHeroes", JSON.stringify([newEntry]));
        } else {
          const parsed = JSON.parse(recentlySearched);
          const filtered = parsed.filter((item) => item.displayName !== newEntry.displayName);
          filtered.unshift(newEntry);
          localStorage.setItem("recentlySearchedHeroes", JSON.stringify(filtered.slice(0, 6)));
        }
      }
    }
  };

  const handleKeyPress = useCallback(
    (e) => {
      if (e.which === 13 && value) {
        search(value);
        inputRef && inputRef.current.blur();
      }
    },
    [search, value],
  );

  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.addEventListener("keypress", handleKeyPress);
      return () => {
        if (inputRef && inputRef.current) {
          inputRef.current.removeEventListener("keypress", handleKeyPress);
        }
      };
    }
  }, [handleKeyPress]);

  const searchInputProps = {
    ref: inputRef,
    placeholder: placeholder || "Search a Hero",
    name: "query",
    value,
    onChange,
    spellCheck: false,
    onBlur,
    onClick,
  };

  return (
    <div className={`${className}`}>
      <div className="masthead-ugg-search-bar autosuggest-container">
        <Autosuggest
          multiSection
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          renderSectionTitle={renderSectionTitle}
          getSectionSuggestions={getSectionSuggestions}
          onSuggestionSelected={onSuggestionSelected}
          onSuggestionHighlighted={onSuggestionHighlighted}
          inputProps={searchInputProps}
        />
        <div className="search-icon">
          <SearchIcon />
        </div>
      </div>
    </div>
  );
}
